import { Match, Participant, Tournament } from '../models';
import { TournamentRepository, ParticipantRepository, MatchRepository } from '../repositories';
import { isValidParticipantCount, getWinnersBracketRounds, getLosersBracketRounds } from '../../core/utils';

interface GeneratedBracket {
  tournament: Tournament;
  matches: Match[];
}

interface TempMatch {
  tournamentId: string;
  round: number;
  matchNumber: number;
  bracketType: 'winners' | 'losers' | 'grand_final';
  participant1Id?: string;
  participant2Id?: string;
  status: 'pending' | 'in_progress' | 'completed';
  nextMatchIndex?: number;
  loserNextMatchIndex?: number;
}

export class GenerateBracketUseCase {
  constructor(
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(tournamentId: string): Promise<GeneratedBracket> {
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    if (tournament.status !== 'setup') {
      throw new Error('Tournament has already started or is completed');
    }

    const participants = await this.participantRepository.getByTournamentId(tournamentId);
    
    if (!isValidParticipantCount(participants.length)) {
      throw new Error(
        `Invalid number of participants: ${participants.length}. Must be a power of 2 (2, 4, 8, 16, etc.)`
      );
    }

    if (participants.length < 2) {
      throw new Error('At least 2 participants are required');
    }

    const sortedParticipants = [...participants].sort((a, b) => {
      const seedA = a.seed ?? Infinity;
      const seedB = b.seed ?? Infinity;
      return seedA - seedB;
    });

    const tempMatches: TempMatch[] = [];

    // Generate all matches
    const winnersMatches = this.generateWinnersBracket(sortedParticipants, tournamentId, tempMatches);
    const losersMatches = this.generateLosersBracket(tournamentId, participants.length, tempMatches, winnersMatches);
    this.generateGrandFinal(tournamentId, tempMatches, winnersMatches, losersMatches);

    // Save and link matches
    const savedMatches = await this.saveAndLinkMatches(tempMatches);

    const updatedTournament = await this.tournamentRepository.update(tournamentId, {
      status: 'in_progress',
    });

    return {
      tournament: updatedTournament,
      matches: savedMatches,
    };
  }

  private generateWinnersBracket(
    participants: Participant[],
    tournamentId: string,
    tempMatches: TempMatch[]
  ): number[][] {
    const winnersRounds = getWinnersBracketRounds(participants.length);
    const matchesByRound: number[][] = [];
    
    for (let round = 1; round <= winnersRounds; round++) {
      const matchesInRound = participants.length / Math.pow(2, round);
      const roundMatches: number[] = [];
      
      for (let matchNum = 0; matchNum < matchesInRound; matchNum++) {
        const matchIndex = tempMatches.length;
        roundMatches.push(matchIndex);
        
        const match: TempMatch = {
          tournamentId,
          round,
          matchNumber: matchNum + 1,
          bracketType: 'winners',
          status: 'pending',
        };

        if (round === 1) {
          match.participant1Id = participants[matchNum * 2].id;
          match.participant2Id = participants[matchNum * 2 + 1].id;
        }

        tempMatches.push(match);
      }
      
      matchesByRound.push(roundMatches);
    }

    // Link winners bracket progression
    for (let round = 0; round < winnersRounds - 1; round++) {
      for (let i = 0; i < matchesByRound[round].length; i++) {
        const currentMatchIndex = matchesByRound[round][i];
        const nextMatchIndex = matchesByRound[round + 1][Math.floor(i / 2)];
        tempMatches[currentMatchIndex].nextMatchIndex = nextMatchIndex;
      }
    }

    return matchesByRound;
  }

  private generateLosersBracket(
    tournamentId: string,
    participantCount: number,
    tempMatches: TempMatch[],
    winnersMatches: number[][]
  ): number[][] {
    const losersMatchesByRound: number[][] = [];
    const losersRounds = getLosersBracketRounds(participantCount);
    const winnersRounds = winnersMatches.length;
    
    // Special case: 2 participants don't need a losers bracket
    if (participantCount === 2) {
      return losersMatchesByRound;
    }
    
    let currentMatchCount = participantCount / 4; // First round of losers
    
    for (let losersRound = 1; losersRound <= losersRounds; losersRound++) {
      const roundMatches: number[] = [];
      
      for (let matchNum = 0; matchNum < currentMatchCount; matchNum++) {
        const matchIndex = tempMatches.length;
        roundMatches.push(matchIndex);
        
        tempMatches.push({
          tournamentId,
          round: losersRound,
          matchNumber: matchNum + 1,
          bracketType: 'losers',
          status: 'pending',
        });
      }
      
      losersMatchesByRound.push(roundMatches);
      
      // Reduce match count every 2 rounds
      if (losersRound % 2 === 0) {
        currentMatchCount = Math.max(1, currentMatchCount / 2);
      }
    }

    // Link losers bracket progression
    for (let round = 0; round < losersMatchesByRound.length - 1; round++) {
      const currentRound = losersMatchesByRound[round];
      const nextRound = losersMatchesByRound[round + 1];
      
      for (let i = 0; i < currentRound.length; i++) {
        const currentMatchIndex = currentRound[i];
        let nextMatchIndex: number;
        
        if (round % 2 === 0) {
          // Odd round (1, 3, 5...): Pair up winners
          nextMatchIndex = nextRound[Math.floor(i / 2)];
        } else {
          // Even round (2, 4, 6...): Direct to same position
          nextMatchIndex = nextRound[i];
        }
        
        if (nextMatchIndex !== undefined) {
          tempMatches[currentMatchIndex].nextMatchIndex = nextMatchIndex;
        }
      }
    }

    // Connect winners losers to losers bracket
    // Round 1 winners losers -> Round 1 losers
    const winnersRound1 = winnersMatches[0];
    const losersRound1 = losersMatchesByRound[0];
    for (let i = 0; i < winnersRound1.length; i++) {
      tempMatches[winnersRound1[i]].loserNextMatchIndex = losersRound1[Math.floor(i / 2)];
    }

    // Subsequent winners losers -> Odd losers rounds (1, 3, 5...)
    for (let winnersRoundIdx = 1; winnersRoundIdx < winnersRounds; winnersRoundIdx++) {
      // Map winners round to losers round: WR2->LR2, WR3->LR4, WR4->LR6, etc.
      const losersRoundIdx = winnersRoundIdx * 2 - 1; // This gives us 1, 3, 5, 7...
      
      if (losersRoundIdx < losersMatchesByRound.length) {
        const winnersRound = winnersMatches[winnersRoundIdx];
        const losersRound = losersMatchesByRound[losersRoundIdx];
        
        for (let i = 0; i < winnersRound.length && i < losersRound.length; i++) {
          tempMatches[winnersRound[i]].loserNextMatchIndex = losersRound[i];
        }
      }
    }

    return losersMatchesByRound;
  }

  private generateGrandFinal(
    tournamentId: string,
    tempMatches: TempMatch[],
    winnersMatches: number[][],
    losersMatches: number[][]
  ): void {
    const grandFinalIndex = tempMatches.length;
    
    tempMatches.push({
      tournamentId,
      round: 1,
      matchNumber: 1,
      bracketType: 'grand_final',
      status: 'pending',
    });

    // Winners final -> Grand final
    const winnersFinalist = winnersMatches[winnersMatches.length - 1][0];
    tempMatches[winnersFinalist].nextMatchIndex = grandFinalIndex;

    // Losers final -> Grand final (if losers bracket exists)
    if (losersMatches.length > 0) {
      const losersFinalist = losersMatches[losersMatches.length - 1][0];
      tempMatches[losersFinalist].nextMatchIndex = grandFinalIndex;
    }
  }

  private async saveAndLinkMatches(tempMatches: TempMatch[]): Promise<Match[]> {
    const savedMatches: Match[] = [];
    const indexToIdMap: { [key: number]: string } = {};

    // First pass: save all matches
    for (let i = 0; i < tempMatches.length; i++) {
      const temp = tempMatches[i];
      const saved = await this.matchRepository.create({
        tournamentId: temp.tournamentId,
        round: temp.round,
        matchNumber: temp.matchNumber,
        bracketType: temp.bracketType,
        participant1Id: temp.participant1Id,
        participant2Id: temp.participant2Id,
        status: temp.status,
      });
      savedMatches.push(saved);
      indexToIdMap[i] = saved.id;
    }

    // Second pass: update matches with connections
    for (let i = 0; i < tempMatches.length; i++) {
      const temp = tempMatches[i];
      const updateData: Partial<Match> = {};

      if (temp.nextMatchIndex !== undefined && indexToIdMap[temp.nextMatchIndex]) {
        updateData.nextMatchId = indexToIdMap[temp.nextMatchIndex];
      }

      if (temp.loserNextMatchIndex !== undefined && indexToIdMap[temp.loserNextMatchIndex]) {
        updateData.loserNextMatchId = indexToIdMap[temp.loserNextMatchIndex];
      }

      if (Object.keys(updateData).length > 0) {
        const updated = await this.matchRepository.update(savedMatches[i].id, updateData);
        savedMatches[i] = updated;
      }
    }

    return savedMatches;
  }
}
